import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useAuthService } from "../../Context/AuthContext";
import { useIsSpeaking } from "@livekit/components-react";
import { displayParticipantAvatar } from "../../Helpers/chatHelper/AvatarHelper";
import { ReactComponent as NotSpeakingParticipantIcon } from "../../Assets/iconsv2/controlButtons/participantNotSeakingIcon.svg"
import { useTranslate } from '@tolgee/react';
import { getOrdinalSuffix } from "../../Helpers/callHelper/mapSpeakOrder";
import { RaisedHandIcon, useIsHandRaised } from "./RaisedHands";

const ParticipantComponentTranslationEnabled = ({ participant, callInfo, grid, focused, speakersQueue }) => {
    const { t } = useTranslate();
    const divRef = useRef(null);
    const handRaised = useIsHandRaised(participant.identity);
    const [vTrack, setVTrack] = useState(null);
    const videoRef = useRef(null);
    const [participantInfoFromCall, setParticipantInfoFromCall] = useState(null);
    const { user } = useAuthService();
    const isUserspeaking = useIsSpeaking(participant);
    const [fontSize, setFontSize] = useState(16);
    const [width, setWidth] = useState(300)
    const [avatarSize, setAvatarSize] = useState(80)
    const [mutedMicrophone, setMutedMicrophone] = useState(false)
    const [orderToSpeak, setOrderToSpeak] = useState("")
    let tracks = participant.getTrackPublications();




    const calculateQueqeOrder = (speakersQueue) => {
        let userOrderNr = speakersQueue?.findIndex((item) => item.user_id == participant.identity)

        if (userOrderNr === 1) {
            setOrderToSpeak(t('call_modal.next_to_speak'))
        } else
            if (userOrderNr > 1) {
                let order = getOrdinalSuffix(userOrderNr);
                setOrderToSpeak(order + " " + t('call_modal.to_speak'))
            } else {
                setOrderToSpeak("")
            }

    }

    const getParticipantInfoFromCall = () => {
        let info;
        let foundInOthers = callInfo.other_participants.find(
            (p) => p.id == participant.identity
        );

        if (participant.identity == callInfo.created_by.id) {
            info = callInfo.created_by;
        } else if (foundInOthers) {
            info = foundInOthers;
        } else {
            info = user;
        }

        if (info) {
            setParticipantInfoFromCall(info);
        }
    };

    useEffect(() => {
        const allTracks = participant.getTrackPublications();
        const audioTrackPub = allTracks.find(
            (track) => track.source === "microphone" && track.track != null
        );

        const handleMuted = () => setMutedMicrophone(true);
        const handleUnmuted = () => setMutedMicrophone(false);

        const handleTrackSubscribed = (track) => {
            if (track.kind === 'audio') {
                setMutedMicrophone(track.isMuted);
                track.on('muted', handleMuted);
                track.on('unmuted', handleUnmuted);
            }
        };

        let audioTrack;

        if (audioTrackPub && audioTrackPub.track) {
            audioTrack = audioTrackPub.track;
            setMutedMicrophone(audioTrack.isMuted);
            audioTrack.on('muted', handleMuted);
            audioTrack.on('unmuted', handleUnmuted);
        }

        participant.on('trackSubscribed', handleTrackSubscribed);

        return () => {
            participant.off('trackSubscribed', handleTrackSubscribed);

            if (audioTrack) {
                audioTrack.off('muted', handleMuted);
                audioTrack.off('unmuted', handleUnmuted);
            }
        };
    }, [participant]);

    useEffect(() => {
        let videoTrack = tracks.find(
            (track) => track.source === "camera" && track.track != null
        );

        if (videoTrack && videoTrack?.track) {
            setVTrack(videoTrack);
            videoTrack.track.attach(videoRef.current);
        } else {
            setVTrack(null);
        }
    }, [tracks]);



    useState(() => {
        getParticipantInfoFromCall();
    }, [participant]);


    useEffect(() => {
        const adjustFontSize = () => {
            if (divRef.current) {
                const { width, height } = divRef.current.getBoundingClientRect();
                // Example: Set font size based on width or height
                const newFontSize = Math.min(width / 10, height / 10, 40); // Adjust divisor and max value as needed
                const newAvatarSize = Math.min(width / 3, height / 3, 80); // Adjust divisor and max value as needed
                setFontSize(newFontSize);
                setWidth(width)
                setAvatarSize(newAvatarSize)
            }
        };

        adjustFontSize(); // Adjust initially
        window.addEventListener("resize", adjustFontSize); // Adjust on resize
        return () => window.removeEventListener("resize", adjustFontSize);
    }, []);

    useEffect(() => {
        calculateQueqeOrder(speakersQueue)
    }, [speakersQueue, participant])

    return (
        <ConnectingContainer ref={divRef}>
            <RaisedHandIcon identity={participant.identity} />
            {vTrack ? (
                <>
                    <VideoContainer>
                        <video ref={videoRef} autoPlay muted playsInline />
                        {mutedMicrophone &&
                            <MutedIconWrapper>
                                <NotSpeakingParticipantIcon />
                            </MutedIconWrapper>
                        }
                        {isUserspeaking &&
                            <SpeakingIconWrapper $handRaised={handRaised}>
                                {t('call_modal.speaking')}
                            </SpeakingIconWrapper>
                        }
                    </VideoContainer>
                    <NameBadge>
                        <NameBadgeText> {participantInfoFromCall.name}</NameBadgeText>
                    </NameBadge>
                </>
            ) : (
                <AvatarWrapper>
                    <UserAvatar avatarSize={avatarSize}>
                        {displayParticipantAvatar(participantInfoFromCall)}
                    </UserAvatar>
                    <Name fontSize={fontSize}>{participantInfoFromCall?.name}</Name>
                    {!isUserspeaking && orderToSpeak !== "" &&
                        <SpeakingIconWrapper $handRaised={handRaised}>
                            {orderToSpeak}
                        </SpeakingIconWrapper>
                    }
                    {mutedMicrophone &&
                        <MutedIconWrapper>
                            <NotSpeakingParticipantIcon />
                        </MutedIconWrapper>
                    }
                    {isUserspeaking &&
                        <SpeakingIconWrapper $handRaised={handRaised}>
                            {t('call_modal.speaking')}
                        </SpeakingIconWrapper>
                    }
                </AvatarWrapper>
            )}
        </ConnectingContainer>
    );
};

export default ParticipantComponentTranslationEnabled;
const ConnectingContainer = styled.div`
  display: flex;
    flex:1 0 calc(25% - 10px);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.callComponent.participantComponentBackground};
  overflow: hidden;
  border-radius: 4px;
  min-height:200px;
    position: relative;
`;

const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-direction: column;
  margin: 5px;
  overflow: hidden;
`;

const UserAvatar = styled.div`
  display: flex;
  align-items: center;
  border-radius: 100px;
  cursor: pointer;
  object-fit: contain;
  position: relative;
  overflow: hidden;

    max-height: ${props => props.avatarSize}px;
    max-width: ${props => props.avatarSize}px;
    min-height: ${props => props.avatarSize}px;
    min-width: ${props => props.avatarSize}px;
  svg {
    position: relative;
    width: 100%;
    height: 100%;
  }

  img {
     display: flex;
    overflow: hidden;
    object-fit: cover;
    width: 100%;
    height: 100%;
    position: relative;
  }
`;

const Name = styled.h3`
font-size:${props => props.fontSize}px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
text-align:center;
`;

const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`
const NameBadge = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.6);
  position: absolute;
  bottom: 10px;
  left: 10px;
  border-radius: 4px;

`;
const NameBadgeText = styled.p`
font-size: 12px;
 color:white;

`
const MutedIconWrapper = styled.div`
display:flex;
align-items:center;
justify-content:center;
position:absolute;
top:20px;
right:20px;
`
const SpeakingIconWrapper = styled.div`
display:flex;
align-items:center;
justify-content:center;
position:absolute;
top:20px;
left:${({ $handRaised }) => ($handRaised ? "44px" : "20px")};
border-radius:4px;
border:1px solid rgba(255, 255, 255, 0.1);
background:${({ theme }) => theme.callComponent.speakingLabelBg};
padding:5px 10px;
p{
font-size:16px;
font-weight:500;
line-height:18px;
color:${({ theme }) => theme.menu.primaryTextColor};
}
`
